import React, { useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import organizationService from '../../services/organization.service';
import {
  Alert,
  Button,
  Card,
  CardHeader,
  Field,
  Input,
  PageHeader,
} from '../ui';

const Settings = () => {
  const [form, setForm] = useState({ name: '', baseCurrency: '', regulatorId: '' });
  const [original, setOriginal] = useState({ name: '', baseCurrency: '', regulatorId: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await organizationService.getInfo();
        const next = {
          name: data?.name || '',
          baseCurrency: data?.baseCurrency || '',
          regulatorId: data?.regulatorId || '',
        };
        setForm(next);
        setOriginal(next);
      } catch {
        setError('Failed to load organization profile.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const dirty =
    form.name !== original.name ||
    form.baseCurrency !== original.baseCurrency ||
    form.regulatorId !== original.regulatorId;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      const payload = {
        name: form.name.trim() || undefined,
        baseCurrency: form.baseCurrency.trim().toUpperCase() || undefined,
        regulatorId: form.regulatorId.trim() || undefined,
      };
      const updated = await organizationService.updateInfo(payload);
      const next = {
        name: updated?.name || '',
        baseCurrency: updated?.baseCurrency || '',
        regulatorId: updated?.regulatorId || '',
      };
      setForm(next);
      setOriginal(next);
      setMessage('Organization profile saved.');
    } catch {
      setError('Failed to save organization profile.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setForm(original);
    setMessage(null);
    setError(null);
  };

  if (loading) {
    return (
      <div>
        <PageHeader title="Settings" description="Manage your organization profile." />
        <Card pad>
          <div className="space-y-4">
            <div className="skeleton h-9" />
            <div className="skeleton h-9" />
            <div className="skeleton h-9" />
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Settings"
        description="Manage your organization profile."
        breadcrumb={['Administration', 'Settings']}
      />

      <div className="max-w-2xl space-y-6">
        {message && <Alert tone="success" title={message} />}
        {error && <Alert tone="danger" title={error} />}

        <form onSubmit={handleSubmit} className="space-y-6">
          <Card pad={false}>
            <CardHeader
              title="Organization profile"
              description="Visible to your team and to Tabadulat operators."
            />
            <div className="p-6 space-y-5">
              <Field label="Organization name" required>
                <Input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  maxLength={120}
                  placeholder="Acme Bank"
                />
              </Field>

              <div className="grid gap-5 sm:grid-cols-2">
                <Field
                  label="Base currency"
                  hint="ISO 4217 code, e.g. USD, AED, EUR"
                >
                  <Input
                    type="text"
                    value={form.baseCurrency}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, baseCurrency: e.target.value.toUpperCase() }))
                    }
                    maxLength={3}
                    placeholder="USD"
                    mono
                  />
                </Field>

                <Field
                  label="Regulator ID"
                  hint="Your regulatory reference (optional)"
                >
                  <Input
                    type="text"
                    value={form.regulatorId}
                    onChange={(e) => setForm((p) => ({ ...p, regulatorId: e.target.value }))}
                    maxLength={120}
                    placeholder="e.g. SAMA-12345"
                  />
                </Field>
              </div>
            </div>
          </Card>

          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-ink-500">
              {dirty ? 'You have unsaved changes' : 'All changes saved'}
            </p>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={handleReset} disabled={!dirty || saving}>
                Discard
              </Button>
              <Button
                type="submit"
                variant="primary"
                iconLeft={Save}
                loading={saving}
                disabled={!dirty}
              >
                Save changes
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Settings;
